/**
 * Pure Sentry scrubbing helpers — no imports, no side effects, unit-testable.
 *
 * The SPA authenticates with a Bearer token and receives it through a magic-link
 * URL (?token=...). Neither must ever reach Sentry. app.js wires scrubEvent as
 * `beforeSend` and scrubBreadcrumb as `beforeBreadcrumb`; both mutate and return
 * the given object (Sentry expects the same reference back, or null to drop).
 */

const REDACTED = '[REDACTED]'

// A Bearer token: "Bearer" then whitespace then the token characters.
const BEARER_RE = /Bearer\s+[A-Za-z0-9._~+/=-]+/gi

// Sensitive auth query params anywhere in a URL: the magic-link token and its
// expiry, and the `signature` of a Laravel signed URL — the business-name change
// link is one, and anyone holding it can change that order's name.
const TOKEN_QUERY_RE = /([?&](?:token|expires|expires_at|access_token|signature)=)[^&#\s]+/gi

// Header/field names dropped wholesale wherever they appear.
const SENSITIVE_KEYS = ['authorization', 'cookie', 'set-cookie', 'x-xsrf-token', 'token', 'access_token', 'bearer']

/**
 * Redact any "Bearer <token>" substring and sensitive query params in a string.
 * Non-strings pass through untouched.
 */
export function redactBearer(value) {
  if (typeof value !== 'string') return value

  return value
    .replace(BEARER_RE, `Bearer ${REDACTED}`)
    .replace(TOKEN_QUERY_RE, `$1${REDACTED}`)
}

/**
 * Redact a URL: strip Bearer/token query params, and drop the whole query on the
 * magic-link verification endpoint (its query IS the one-time token).
 */
export function redactUrl(url) {
  if (typeof url !== 'string') return url

  let out = redactBearer(url)

  if (out.includes('/api/client/auth/verify')) {
    out = out.replace(/(\/api\/client\/auth\/verify)\?[^#\s]*/i, `$1?${REDACTED}`)
  }

  // Same reasoning for the "choose another business name" link and the endpoints
  // it signs: the query IS the capability, so the whole thing goes.
  if (out.includes('/business-name/change') || out.includes('/api/bn/name-change')) {
    out = out.replace(/(\/business-name\/change|\/api\/bn\/name-change[^?#\s]*)\?[^#\s]*/i, `$1?${REDACTED}`)
  }

  return out
}

/**
 * Drop sensitive headers in place and redact Bearer tokens from the rest.
 * Header keys are matched case-insensitively.
 */
function scrubHeaders(headers) {
  if (!headers || typeof headers !== 'object') return headers

  for (const key of Object.keys(headers)) {
    if (SENSITIVE_KEYS.includes(key.toLowerCase())) {
      delete headers[key]
    } else {
      headers[key] = redactBearer(headers[key])
    }
  }

  return headers
}

/**
 * Bounded recursive redactor for free-form bags (extra, contexts): redacts
 * strings, drops sensitive keys, and recurses into objects/arrays up to a depth
 * cap so a cyclic or huge payload cannot hang the scrubber.
 */
function redactDeep(value, depth = 0) {
  if (value == null || depth > 6) return value

  if (typeof value === 'string') return redactBearer(value)

  if (Array.isArray(value)) {
    return value.map((item) => redactDeep(item, depth + 1))
  }

  if (typeof value === 'object') {
    for (const key of Object.keys(value)) {
      if (SENSITIVE_KEYS.includes(key.toLowerCase())) {
        value[key] = REDACTED
      } else {
        value[key] = redactDeep(value[key], depth + 1)
      }
    }

    return value
  }

  return value
}

/**
 * Scrub a Sentry event before it is sent: remove auth headers/cookies, and
 * redact Bearer tokens and magic-link tokens from the URL, exception values,
 * message, breadcrumbs, extra and contexts.
 */
export function scrubEvent(event) {
  if (!event || typeof event !== 'object') return event

  if (event.request && typeof event.request === 'object') {
    scrubHeaders(event.request.headers)

    if (typeof event.request.url === 'string') event.request.url = redactUrl(event.request.url)
    if (typeof event.request.query_string === 'string') event.request.query_string = redactBearer(event.request.query_string)
    if (typeof event.request.data === 'string') event.request.data = redactBearer(event.request.data)

    delete event.request.cookies
  }

  if (typeof event.message === 'string') event.message = redactBearer(event.message)

  const values = event.exception?.values
  if (Array.isArray(values)) {
    for (const item of values) {
      if (item && typeof item.value === 'string') item.value = redactBearer(item.value)
    }
  }

  // Breadcrumbs can ride along on the event (Sentry stores them as {values:[]}).
  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb)
  } else if (event.breadcrumbs && Array.isArray(event.breadcrumbs.values)) {
    event.breadcrumbs.values = event.breadcrumbs.values.map(scrubBreadcrumb)
  }

  if (event.extra) event.extra = redactDeep(event.extra)
  if (event.contexts) event.contexts = redactDeep(event.contexts)

  return event
}

/**
 * Scrub a single breadcrumb: for xhr/fetch crumbs redact the Bearer/token in the
 * URL (and the magic-link query), drop any auth header, and redact captured
 * request/response bodies.
 */
export function scrubBreadcrumb(breadcrumb) {
  if (!breadcrumb || typeof breadcrumb !== 'object') return breadcrumb

  const data = breadcrumb.data

  if (data && typeof data === 'object') {
    if (typeof data.url === 'string') data.url = redactUrl(data.url)
    if (typeof data.body === 'string') data.body = redactBearer(data.body)
    if (typeof data.request_body === 'string') data.request_body = redactBearer(data.request_body)
    if (typeof data.response_body === 'string') data.response_body = redactBearer(data.response_body)

    scrubHeaders(data.headers)
  }

  if (typeof breadcrumb.message === 'string') breadcrumb.message = redactUrl(breadcrumb.message)

  return breadcrumb
}

// Whole message only: "Failed to fetch dynamically imported module" is our own deploy skew.
const NETWORK_FAILURE_RE = /^(?:Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.?)(?: \([^)]*\))?$/i
const THIRD_PARTY_FRAME_RE = /^(?:chrome|moz|safari-web)-extension:\/\/|googletagmanager\.com|google-analytics\.com|\/gtag\/js/i

// Fetch failures from analytics beacons or extensions; denyUrls misses them because the
// innermost frame is the SDK's fetch wrapper in our bundle. The app itself only uses axios (XHR).
export function isThirdPartyFetchNoise(event) {
  const values = event?.exception?.values
  if (!Array.isArray(values)) return false

  return values.some((item) => {
    if (typeof item?.value !== 'string' || !NETWORK_FAILURE_RE.test(item.value)) return false

    const frames = item.stacktrace?.frames
    if (!Array.isArray(frames)) return false

    return frames.some((frame) => [frame?.filename, frame?.abs_path]
      .some((path) => typeof path === 'string' && THIRD_PARTY_FRAME_RE.test(path)))
  })
}
